import React, { useState, useMemo, useCallback } from 'react'
import { Formik, Form } from 'formik'
import AddUserModalForm from './AddUserModalForm'
import StutterStepModal from 'components/StutterStepModal/index'
import { singleAddUserSchema, bulkAddUserSchema } from 'lib/schemas'
import { AddUsersRolesInfo, inputModeType, parseEmailString } from 'lib/util'
import {
  DATA_LOSS_SCREEN_TEXT_SINGLE_USER,
  DATA_LOSS_SCREEN_HEADER_SINGLE_USER,
  DATA_LOSS_SCREEN_TEXT_MULTIPLE_USERS,
  DATA_LOSS_SCREEN_HEADER_MULTIPLE_USERS,
} from './constants'
import { Site } from 'types'
import { FormValues, FormErrors, RoleInfo } from './types'

interface AddUserModalFormContainerProps {
  handleCloseAddUsersModal: () => void
  handleSubmitAddUsers: (
    emailArray: string[],
    selectedRole: string,
    sendNotification: boolean,
    inputMode: string,
  ) => void
  selectedSite?: Site
}

const AddUserModalFormContainer: React.FC<AddUserModalFormContainerProps> = ({
  handleCloseAddUsersModal,
  handleSubmitAddUsers,
  selectedSite,
}) => {
  const [openStutterModal, setOpenStutterModal] = useState(false)
  const [inputMode, setInputMode] = useState(inputModeType.single)
  const [submitClicked, setSubmitClicked] = useState(false)

  const isSingleMode = inputMode === inputModeType.single

  const hasADLSkuCode = useMemo(
    () =>
      selectedSite?.netSuiteAssets?.some(
        netSuiteAsset => (netSuiteAsset.product as any).skuCode === 'ADL',
      ) ?? false,
    [selectedSite],
  )

  const rolesInfo: RoleInfo[] = useMemo(
    () => (hasADLSkuCode ? AddUsersRolesInfo.ADL : AddUsersRolesInfo.Default),
    [hasADLSkuCode],
  )

  const validationSchema = useMemo(
    () => (isSingleMode ? singleAddUserSchema : bulkAddUserSchema),
    [isSingleMode],
  )

  const stutterModalContent = useMemo(
    () => ({
      header: isSingleMode
        ? DATA_LOSS_SCREEN_HEADER_MULTIPLE_USERS
        : DATA_LOSS_SCREEN_HEADER_SINGLE_USER,
      text: isSingleMode
        ? DATA_LOSS_SCREEN_TEXT_MULTIPLE_USERS
        : DATA_LOSS_SCREEN_TEXT_SINGLE_USER,
    }),
    [isSingleMode],
  )

  const handleCloseModal = useCallback(
    (resetForm: () => void) => {
      resetForm()
      handleCloseAddUsersModal()
    },
    [handleCloseAddUsersModal],
  )

  const toggleInputMode = useCallback(() => {
    setInputMode(prev =>
      prev === inputModeType.single ? inputModeType.bulk : inputModeType.single,
    )
    setSubmitClicked(false)
  }, [])

  const handleInputModeToggle = useCallback(
    (emails: string, resetForm: () => void, confirmClicked = false) => {
      if (emails.length === 0 || confirmClicked) {
        toggleInputMode()
        resetForm()
      } else {
        setOpenStutterModal(true)
      }
    },
    [toggleInputMode],
  )

  const onConfirmStutterModal = useCallback(
    (emails: string, resetForm: () => void) => {
      setOpenStutterModal(false)
      handleInputModeToggle(emails, resetForm, true)
    },
    [handleInputModeToggle],
  )

  const onCancelStutterModal = useCallback(() => {
    setOpenStutterModal(false)
  }, [])

  const handleFormSubmit = useCallback(
    (
      values: FormValues,
      { setSubmitting }: { setSubmitting: (value: boolean) => void },
    ) => {
      const emailArray = parseEmailString(values.emails, { lowercase: true })
      const sendNotification = values.sendNotification === 'true'

      if (emailArray.length > 0) {
        handleSubmitAddUsers(
          emailArray,
          values.selectedRole,
          sendNotification,
          inputMode,
        )
      }

      setSubmitting(false)
    },
    [handleSubmitAddUsers, inputMode],
  )

  const initialValues: FormValues = useMemo(
    () => ({
      emails: '',
      selectedRole: 'Teacher',
      sendNotification: 'true',
    }),
    [],
  )

  return (
    <Formik<FormValues>
      {...({ context: { inputMode } } as any)}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validateOnBlur={false}
      validateOnChange={submitClicked}
      validationSchema={validationSchema}
    >
      {({ values, errors, resetForm, isSubmitting }) => (
        <Form {...({} as any)}>
          <AddUserModalForm
            errors={errors as FormErrors}
            handleCloseModal={() => handleCloseModal(resetForm)}
            handleInputModeToggle={() =>
              handleInputModeToggle(values.emails, resetForm, false)
            }
            inputMode={inputMode}
            isSubmitting={isSubmitting}
            rolesInfo={rolesInfo}
            setSubmitClicked={setSubmitClicked}
            values={values}
          />
          <StutterStepModal
            header={stutterModalContent.header}
            isOpen={openStutterModal}
            onCancel={onCancelStutterModal}
            onConfirm={() => onConfirmStutterModal(values.emails, resetForm)}
            text={stutterModalContent.text}
          />
        </Form>
      )}
    </Formik>
  )
}

export default AddUserModalFormContainer
